import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CircleMarker, MapContainer, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { toCanvas as htmlToImageCanvas } from 'html-to-image';
import { Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './LevantamentoPosicoesModule.css';

interface PositionPoint {
  latitude: number;
  longitude: number;
  timestamp: number | null;
  placa: string | null;
  velocidade: number | null;
  endereco: string | null;
  ordemOriginal: number;
}

interface CvaResumo {
  cva: string;
  vale_pedagio: string;
  data_pedido: string;
  cupons_concedidos: number;
  cupons_utilizados: number;
  cupons_nao_contabilizados: number;
  placa_do_veiculo?: string;
}

interface CvaVisaoGeralRecord {
  cva: string;
  vale_pedagio: string;
  data_pedido: string;
  placa_do_veiculo?: string;
  valor_cva?: number | string;
  cupons_concedidos: number | string;
  cupons_utilizados: number | string;
  total_credito_concedido?: number | string;
  cupons_nao_contabilizados: number | string;
  tem_alerta_sem_vinculo?: number;
}

interface CvaVisaoGeralResumo {
  quantidade_cva: number;
  cupons_concedidos: number;
  cupons_utilizados: number;
  cupons_nao_contabilizados: number;
  valor_concedido: number;
  valor_creditado: number;
  diferenca_valor: number;
}

interface FaturaRecord {
  numero_da_fatura: string;
  data: string;
  horario: string;
  placa_veiculo?: string;
  tipo_do_veiculo: string;
  descricao: string;
  tipo_de_uso: string;
  valor_rs: number | string;
  debito_credito: string;
  viagem_de_vale_pedagio: string;
}

interface CobrancaPracaFaltanteRegistro {
  numero_da_fatura: string;
  data: string;
  horario: string;
  placa_veiculo?: string;
  descricao: string;
  tipo_de_uso: string;
  debito_credito: string;
  valor_rs: number;
}

interface CobrancaPracaFaltante {
  praca: string;
  quantidade: number;
  valor_total: number;
  registros: CobrancaPracaFaltanteRegistro[];
}

interface CobrancasPracasFaltantesResponse {
  placa_do_veiculo?: string;
  pracas_faltantes: string[];
  cobrancas_sem_vinculo: CobrancaPracaFaltante[];
  total_valor_sem_vinculo: number;
}

const parseNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const cleaned = value
      .trim()
      .replace(/\s+/g, '')
      .replace(/[^0-9,.-]/g, '');

    if (!cleaned) {
      return null;
    }

    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    let normalized = cleaned;

    // Trata formatos como 1.234,56 e 1,234.56.
    if (hasComma && hasDot) {
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');

      if (lastComma > lastDot) {
        normalized = cleaned.replace(/\./g, '').replace(',', '.');
      } else {
        normalized = cleaned.replace(/,/g, '');
      }
    } else if (hasComma) {
      normalized = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // Se houver mais de um ponto, assume separador de milhar e remove todos.
      const dotCount = (cleaned.match(/\./g) || []).length;
      normalized = dotCount > 1 ? cleaned.replace(/\./g, '') : cleaned;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeCoordinate = (value: unknown, limit: number): number | null => {
  const parsed = parseNumber(value);
  if (parsed === null) {
    return null;
  }

  if (Math.abs(parsed) <= limit) {
    return parsed;
  }

  // Alguns arquivos vêm em coordenada escalada (ex.: micrograus).
  for (let scale = 10; scale <= 1000000000000; scale *= 10) {
    const scaled = parsed / scale;
    if (Math.abs(scaled) <= limit) {
      return scaled;
    }
  }

  return null;
};

const parseTimestamp = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    // Serial date do Excel (dias desde 1899-12-30).
    const serialAsMs = Math.round((value - 25569) * 86400 * 1000);
    if (!Number.isNaN(serialAsMs)) {
      return serialAsMs;
    }
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const direct = Date.parse(trimmed);
    if (!Number.isNaN(direct)) {
      return direct;
    }

    const brPattern =
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
    const match = trimmed.match(brPattern);

    if (match) {
      const day = Number(match[1]);
      const month = Number(match[2]) - 1;
      let year = Number(match[3]);
      const hour = Number(match[4] || 0);
      const minute = Number(match[5] || 0);
      const second = Number(match[6] || 0);

      if (year < 100) {
        year += 2000;
      }

      const parsedDate = new Date(year, month, day, hour, minute, second);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
    }
  }

  return null;
};

const toReadableDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) {
    return '-';
  }

  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} min`;
  }

  return `${hours}h ${minutes}min`;
};

const haversineKm = (a: PositionPoint, b: PositionPoint): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);

  return 2 * R * Math.asin(Math.sqrt(h));
};

const formatDateTime = (timestamp: number | null): string => {
  if (!timestamp) {
    return '-';
  }

  return new Date(timestamp).toLocaleString('pt-BR');
};

const sampleCoordinates = (coords: Array<[number, number]>, maxPoints = 320): Array<[number, number]> => {
  if (coords.length <= maxPoints) {
    return coords;
  }

  const sampled: Array<[number, number]> = [];
  const step = (coords.length - 1) / (maxPoints - 1);

  for (let i = 0; i < maxPoints; i += 1) {
    const index = Math.round(i * step);
    sampled.push(coords[index]);
  }

  return sampled;
};

const reduceCoordinateNoise = (
  coords: Array<[number, number]>,
  minDistanceKm = 0.12
): Array<[number, number]> => {
  if (coords.length <= 2) {
    return coords;
  }

  const reduced: Array<[number, number]> = [coords[0]];

  for (let i = 1; i < coords.length - 1; i += 1) {
    const prev = reduced[reduced.length - 1];
    const curr = coords[i];
    const prevPoint: PositionPoint = {
      latitude: prev[0],
      longitude: prev[1],
      timestamp: null,
      placa: null,
      velocidade: null,
      endereco: null,
      ordemOriginal: 0
    };
    const currPoint: PositionPoint = {
      latitude: curr[0],
      longitude: curr[1],
      timestamp: null,
      placa: null,
      velocidade: null,
      endereco: null,
      ordemOriginal: 0
    };

    const distance = haversineKm(prevPoint, currPoint);
    if (distance >= minDistanceKm) {
      reduced.push(curr);
    }
  }

  reduced.push(coords[coords.length - 1]);
  return reduced;
};

const flattenSegments = (segments: Array<Array<[number, number]>>): Array<[number, number]> => {
  const flat: Array<[number, number]> = [];
  for (const seg of segments) {
    for (const point of seg) {
      flat.push(point);
    }
  }
  return flat;
};

const splitPointsIntoSegments = (points: PositionPoint[]): Array<Array<[number, number]>> => {
  if (points.length < 2) {
    return [];
  }

  const segments: Array<Array<[number, number]>> = [];
  let current: Array<[number, number]> = [[points[0].latitude, points[0].longitude]];

  for (let i = 1; i < points.length; i += 1) {
    const prev = points[i - 1];
    const curr = points[i];

    const distanceKm = haversineKm(prev, curr);
    const hasTimestamps = prev.timestamp !== null && curr.timestamp !== null;
    const deltaMinutes = hasTimestamps ? Math.max((curr.timestamp! - prev.timestamp!) / 60000, 0) : null;
    const inferredSpeed = deltaMinutes && deltaMinutes > 0 ? distanceKm / (deltaMinutes / 60) : null;

    const largeJump = distanceKm > 30;
    const impossibleShortInterval = distanceKm > 8 && deltaMinutes !== null && deltaMinutes <= 5;
    const impossibleSpeed = inferredSpeed !== null && inferredSpeed > 180;

    if (largeJump || impossibleShortInterval || impossibleSpeed) {
      if (current.length >= 2) {
        segments.push(current);
      }
      current = [[curr.latitude, curr.longitude]];
      continue;
    }

    current.push([curr.latitude, curr.longitude]);
  }

  if (current.length >= 2) {
    segments.push(current);
  }

  return segments;
};

const buildChunks = (coords: Array<[number, number]>, chunkSize = 90): Array<Array<[number, number]>> => {
  if (coords.length <= chunkSize) {
    return [coords];
  }

  const chunks: Array<Array<[number, number]>> = [];
  let start = 0;

  while (start < coords.length) {
    const end = Math.min(start + chunkSize, coords.length);
    const slice = coords.slice(start, end);

    if (slice.length >= 2) {
      chunks.push(slice);
    }

    if (end === coords.length) {
      break;
    }

    // Reaproveita o último ponto para manter continuidade entre chunks.
    start = end - 1;
  }

  return chunks;
};

const mergePolylines = (polylines: Array<Array<[number, number]>>): Array<[number, number]> => {
  const merged: Array<[number, number]> = [];

  for (const polyline of polylines) {
    for (const point of polyline) {
      const prev = merged[merged.length - 1];
      if (!prev || prev[0] !== point[0] || prev[1] !== point[1]) {
        merged.push(point);
      }
    }
  }

  return merged;
};

const haversineCoordsKm = (a: [number, number], b: [number, number]): number => {
  const pointA: PositionPoint = {
    latitude: a[0],
    longitude: a[1],
    timestamp: null,
    placa: null,
    velocidade: null,
    endereco: null,
    ordemOriginal: 0
  };
  const pointB: PositionPoint = {
    latitude: b[0],
    longitude: b[1],
    timestamp: null,
    placa: null,
    velocidade: null,
    endereco: null,
    ordemOriginal: 0
  };

  return haversineKm(pointA, pointB);
};

const bridgeNearbySegmentGaps = (
  segments: Array<Array<[number, number]>>,
  maxGapKm = 3
): Array<Array<[number, number]>> => {
  if (segments.length <= 1) {
    return segments;
  }

  const bridged: Array<Array<[number, number]>> = [];
  for (let i = 0; i < segments.length; i += 1) {
    const current = segments[i];
    if (current.length < 2) {
      continue;
    }

    if (bridged.length > 0) {
      const prev = bridged[bridged.length - 1];
      const prevEnd = prev[prev.length - 1];
      const currentStart = current[0];
      const gapKm = haversineCoordsKm(prevEnd, currentStart);

      // Preenche apenas lacunas pequenas para evitar ligar trechos realmente desconexos.
      if (gapKm > 0.05 && gapKm <= maxGapKm) {
        bridged.push([prevEnd, currentStart]);
      }
    }

    bridged.push(current);
  }

  return bridged;
};

const fetchJsonWithTimeout = async (
  url: string,
  signal?: AbortSignal,
  timeoutMs = 8000
): Promise<Response> => {
  const timeoutPromise = new Promise<Response>((_, reject) => {
    const id = setTimeout(() => {
      clearTimeout(id);
      reject(new Error(`Timeout após ${timeoutMs}ms`));
    }, timeoutMs);
  });

  const fetchPromise = fetch(url, { signal });
  return Promise.race([fetchPromise, timeoutPromise]);
};

const fetchRoadRouteChunk = async (
  coords: Array<[number, number]>,
  signal?: AbortSignal
): Promise<Array<[number, number]>> => {
  if (coords.length < 2) {
    return coords;
  }

  const coordParam = coords.map(([lat, lng]) => `${lng},${lat}`).join(';');
  const url = `https://router.project-osrm.org/route/v1/driving/${coordParam}?overview=full&geometries=geojson`;

  const response = await fetchJsonWithTimeout(url, signal, 9000);
  if (!response.ok) {
    throw new Error(`OSRM chunk route falhou: ${response.status}`);
  }

  const data = await response.json();
  const coordsGeoJson = data?.routes?.[0]?.geometry?.coordinates;
  if (!Array.isArray(coordsGeoJson) || coordsGeoJson.length < 2) {
    throw new Error('OSRM chunk sem geometria válida.');
  }

  const polyline: Array<[number, number]> = [];
  for (const c of coordsGeoJson) {
    if (Array.isArray(c) && c.length >= 2) {
      const lng = Number(c[0]);
      const lat = Number(c[1]);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        polyline.push([lat, lng]);
      }
    }
  }

  if (polyline.length < 2) {
    throw new Error('OSRM chunk vazio após parse.');
  }

  return polyline;
};

const fetchRoadRouteChunkWithRetry = async (
  coords: Array<[number, number]>,
  signal?: AbortSignal,
  retries = 1
): Promise<Array<[number, number]>> => {
  let lastError: unknown = null;

  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await fetchRoadRouteChunk(coords, signal);
    } catch (error) {
      lastError = error;
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 350));
      }
    }
  }

  throw lastError;
};

const processChunksWithConcurrency = async (
  chunks: Array<Array<[number, number]>>,
  signal?: AbortSignal,
  concurrency = 4
): Promise<Array<Array<[number, number]>>> => {
  const results: Array<Array<[number, number]>> = new Array(chunks.length).fill([]);
  let cursor = 0;

  const worker = async () => {
    while (cursor < chunks.length) {
      const index = cursor;
      cursor += 1;

      try {
        const polyline = await fetchRoadRouteChunkWithRetry(chunks[index], signal, 1);
        results[index] = polyline;
      } catch (error) {
        console.warn('Falha ao rotear chunk por estrada; usando linha aproximada:', error);
        // Evita buracos na rota quando um trecho não consegue resposta do OSRM.
        results[index] = chunks[index];
      }
    }
  };

  const workers = Array.from({ length: Math.min(concurrency, chunks.length) }, () => worker());
  await Promise.all(workers);
  return results;
};

const fetchRoadRoutedPolyline = async (
  coords: Array<[number, number]>,
  signal?: AbortSignal
): Promise<Array<[number, number]>> => {
  if (coords.length < 2) {
    return coords;
  }

  const reduced = reduceCoordinateNoise(coords, 0.12);
  const sampled = sampleCoordinates(reduced, 260);
  const chunks = buildChunks(sampled, 35);

  const orderedPolylines = (await processChunksWithConcurrency(chunks, signal, 4))
    .filter(poly => poly.length >= 2);

  const merged = mergePolylines(orderedPolylines);
  if (merged.length < 2) {
    throw new Error('Nenhum trecho viário válido retornado.');
  }

  return merged;
};

const sanitizeFilePart = (value: string): string => {
  return value.replace(/[^a-zA-Z0-9_-]/g, '_');
};

const summaryFieldValue = (value: unknown): string => {
  if (value === null || value === undefined) return '-';
  const text = String(value).trim();
  return text ? text : '-';
};

const formatWholeNumber = (value: unknown): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '0';
  }

  return Math.trunc(num).toLocaleString('pt-BR');
};

const formatCurrencyValue = (value: unknown): string => {
  const num = parseNumber(value);
  if (num === null) {
    return 'R$ 0,00';
  }

  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const calculateCurrencyDifference = (leftValue: unknown, rightValue: unknown): number => {
  const left = parseNumber(leftValue) || 0;
  const right = parseNumber(rightValue) || 0;
  return left - right;
};

const escapeCsvCell = (value: unknown): string => {
  const text = value === null || value === undefined ? '' : String(value);
  const escaped = text.replace(/"/g, '""');
  return `"${escaped}"`;
};

const FILTRO_PRACA_TOTAL_VW = '__TOTAL_GERAL_PRACAS_VW__';
const FILTRO_PRACA_OUTRAS = '__OUTRAS_PRACAS__';

const wait = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

const waitForLeafletTiles = async (container: HTMLElement, timeoutMs = 2500): Promise<void> => {
  const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
  if (images.length === 0) {
    return;
  }

  await Promise.race([
    Promise.all(images.map(img => {
      if (img.complete) {
        return Promise.resolve();
      }

      return new Promise<void>(resolve => {
        const done = () => resolve();
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      });
    })),
    wait(timeoutMs)
  ]);
};

const captureMapCanvas = async (targetElement: HTMLElement): Promise<HTMLCanvasElement> => {
  try {
    const canvas = await htmlToImageCanvas(targetElement, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: '#ffffff'
    });

    if (canvas.width > 0 && canvas.height > 0) {
      return canvas;
    }
  } catch (error) {
    console.warn('html-to-image falhou na captura do mapa, tentando fallback:', error);
  }

  return html2canvas(targetElement, {
    useCORS: true,
    allowTaint: false,
    scale: 2,
    backgroundColor: '#ffffff',
    logging: false
  });
};

const fetchImageAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Falha ao converter imagem para base64.'));
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn('Falha ao carregar imagem para PDF:', error);
    return null;
  }
};

const MapAutoFit: React.FC<{ coordinates: Array<[number, number]> }> = ({ coordinates }) => {
  const map = useMap();

  React.useEffect(() => {
    if (coordinates.length === 0) {
      return;
    }

    if (coordinates.length === 1) {
      map.setView(coordinates[0], 14);
      return;
    }

    map.fitBounds(coordinates, {
      padding: [20, 20]
    });
  }, [coordinates, map]);

  return null;
};

const MapInstanceBridge: React.FC<{ onMapReady: (map: LeafletMap) => void }> = ({ onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    onMapReady(map);
  }, [map, onMapReady]);

  return null;
};

const LevantamentoPosicoesModule: React.FC = () => {
  const [placaFiltro, setPlacaFiltro] = useState<string>('');
  const [cvaFiltro, setCvaFiltro] = useState<string>('');
  const [placasDisponiveis, setPlacasDisponiveis] = useState<string[]>([]);
  const [tiposVeiculoDisponiveis, setTiposVeiculoDisponiveis] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [allPoints, setAllPoints] = useState<PositionPoint[]>([]);
  const [placas, setPlacas] = useState<string[]>([]);
  const [placaSelecionada, setPlacaSelecionada] = useState<string>('TODAS');
  const [statusMessage, setStatusMessage] = useState<string>('Informe o CVA ou a placa para buscar a rota.');
  const [roadPolylines, setRoadPolylines] = useState<Array<Array<[number, number]>>>([]);
  const [roadStatus, setRoadStatus] = useState<string>('');
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);
  const [importingExcel, setImportingExcel] = useState<boolean>(false);
  const [selectedExcelFiles, setSelectedExcelFiles] = useState<File[]>([]);
  const [importingFaturaExcel, setImportingFaturaExcel] = useState<boolean>(false);
  const [selectedFaturaExcelFiles, setSelectedFaturaExcelFiles] = useState<File[]>([]);
  const [resumoCva, setResumoCva] = useState<CvaResumo | null>(null);
  const [faturaRecords, setFaturaRecords] = useState<FaturaRecord[]>([]);
  const [visaoGeralRecords, setVisaoGeralRecords] = useState<(FaturaRecord & { placa_veiculo?: string })[]>([]);
  const [visaoGeralTotal, setVisaoGeralTotal] = useState<number>(0);
  const [visaoGeralPage, setVisaoGeralPage] = useState<number>(1);
  const [visaoGeralLimit, setVisaoGeralLimit] = useState<number>(50);
  const [loadingVisaoGeral, setLoadingVisaoGeral] = useState<boolean>(false);
  const [exportandoVisaoGeral, setExportandoVisaoGeral] = useState<boolean>(false);
  const [loadingVisaoGeralCva, setLoadingVisaoGeralCva] = useState<boolean>(false);
  const [visaoGeralFaturaFiltro, setVisaoGeralFaturaFiltro] = useState<string>('');
  const [visaoGeralTipoVeiculoFiltro, setVisaoGeralTipoVeiculoFiltro] = useState<string>('');
  const [visaoGeralDataInicio, setVisaoGeralDataInicio] = useState<string>('');
  const [visaoGeralDataFim, setVisaoGeralDataFim] = useState<string>('');
  const [visaoGeralCvaFiltro, setVisaoGeralCvaFiltro] = useState<string>('');
  const [visaoGeralCvaDataInicio, setVisaoGeralCvaDataInicio] = useState<string>('');
  const [visaoGeralCvaDataFim, setVisaoGeralCvaDataFim] = useState<string>('');
  const [visaoGeralCvaStatusUtilizacao, setVisaoGeralCvaStatusUtilizacao] = useState<string>('');
  const [visaoGeralCvaSomenteAlerta, setVisaoGeralCvaSomenteAlerta] = useState<boolean>(false);
  const [visaoGeralCvaRecords, setVisaoGeralCvaRecords] = useState<CvaVisaoGeralRecord[]>([]);
  const [visaoGeralCvaTotal, setVisaoGeralCvaTotal] = useState<number>(0);
  const [visaoGeralCvaPage, setVisaoGeralCvaPage] = useState<number>(1);
  const [visaoGeralCvaLimit, setVisaoGeralCvaLimit] = useState<number>(50);
  const [exportandoVisaoGeralCva, setExportandoVisaoGeralCva] = useState<boolean>(false);
  const [visaoGeralCvaSortBy, setVisaoGeralCvaSortBy] = useState<string>('');
  const [visaoGeralCvaSortDir, setVisaoGeralCvaSortDir] = useState<'asc' | 'desc'>('desc');
  const [expandedCvaKey, setExpandedCvaKey] = useState<string | null>(null);
  const [expandedCvaFatura, setExpandedCvaFatura] = useState<FaturaRecord[]>([]);
  const [expandedCvaPracasFaltantes, setExpandedCvaPracasFaltantes] = useState<string[]>([]);
  const [expandedCvaCobrancasFaltantes, setExpandedCvaCobrancasFaltantes] = useState<CobrancaPracaFaltante[]>([]);
  const [expandedCvaTotalSemVinculo, setExpandedCvaTotalSemVinculo] = useState<number>(0);
  const [detalhesSelecionados, setDetalhesSelecionados] = useState<string[]>([]);
  const [mostrarSomenteSelecionados, setMostrarSomenteSelecionados] = useState<boolean>(false);
  const [selecaoSalvaMensagem, setSelecaoSalvaMensagem] = useState<string>('');
  const [loadingExpandedCva, setLoadingExpandedCva] = useState<boolean>(false);
  const [visaoGeralCvaResumo, setVisaoGeralCvaResumo] = useState<CvaVisaoGeralResumo>({
    quantidade_cva: 0,
    cupons_concedidos: 0,
    cupons_utilizados: 0,
    cupons_nao_contabilizados: 0,
    valor_concedido: 0,
    valor_creditado: 0,
    diferenca_valor: 0
  });
  const [passagensProPraca, setPassagensProPraca] = useState<Array<{ praca: string; total: number; valorTotal: number }>>([]);
  const [loadingPassagensProPraca, setLoadingPassagensProPraca] = useState<boolean>(false);
  const [pracaSelecionada, setPracaSelecionada] = useState<string>('');
  const [view, setView] = useState<'principal' | 'visaoGeral' | 'visaoGeralCva'>('principal');
  const mapExportRef = useRef<HTMLDivElement | null>(null);
  const leafletMapRef = useRef<LeafletMap | null>(null);
  const excelInputRef = useRef<HTMLInputElement | null>(null);
  const faturaExcelInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const cva = cvaFiltro.trim();
    if (!cva) return;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/levantamento-posicoes/resumo-cva?cva=${encodeURIComponent(cva)}`);
        if (res.ok) {
          const data: CvaResumo = await res.json();
          if (data.placa_do_veiculo) {
            setPlacaFiltro(data.placa_do_veiculo.trim().toUpperCase());
          }
        }
      } catch {
        // silencioso
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [cvaFiltro]);

  const carregarPlacas = async () => {
    try {
      const query = placaFiltro.trim();
      const res = await fetch(`/api/levantamento-posicoes/placas?termo=${encodeURIComponent(query)}`);
      if (!res.ok) {
        return;
      }

      const data: string[] = await res.json();
      setPlacasDisponiveis(data || []);
    } catch (error) {
      console.error('Erro ao carregar placas:', error);
    }
  };

  const carregarTiposVeiculo = async () => {
    if (tiposVeiculoDisponiveis.length > 0) {
      return;
    }

    try {
      const res = await fetch('/api/levantamento-posicoes/tipos-veiculo');
      if (!res.ok) {
        return;
      }

      const data: string[] = await res.json();
      setTiposVeiculoDisponiveis(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar tipos de veículo:', error);
    }
  };

  useEffect(() => {
    if (view === 'visaoGeral') {
      carregarTiposVeiculo();
    }
  }, [view]);

  const buscarPosicoes = async () => {
    if (!cvaFiltro.trim()) {
      setStatusMessage('Informe o CVA para buscar os dados.');
      return;
    }

    setLoading(true);
    setResumoCva(null);
    setFaturaRecords([]);
    setStatusMessage('Consultando dados do CVA...');

    try {
      const res = await fetch(`/api/levantamento-posicoes/resumo-cva?cva=${encodeURIComponent(cvaFiltro.trim())}`);
      if (res.ok) {
        const resumoData: CvaResumo = await res.json();
        setResumoCva(resumoData);
        if (resumoData.placa_do_veiculo) {
          setPlacaFiltro(resumoData.placa_do_veiculo.trim().toUpperCase());
        }

        // Buscar dados da fatura
        const resFatura = await fetch(`/api/levantamento-posicoes/fatura-por-cva?cva=${encodeURIComponent(cvaFiltro.trim())}`);
        if (resFatura.ok) {
          const faturaData: FaturaRecord[] = await resFatura.json();
          setFaturaRecords(Array.isArray(faturaData) ? faturaData : []);
        }

        setStatusMessage('Dados encontrados.');
      } else {
        const err = await res.json().catch(() => ({ message: 'CVA não encontrado.' }));
        setStatusMessage(err.message || 'CVA não encontrado.');
      }
    } catch (error) {
      console.error('Erro ao buscar dados do CVA:', error);
      setStatusMessage('Erro ao consultar dados do CVA.');
    } finally {
      setLoading(false);
    }
  };

  const buscarVisaoGeral = async (
    page = 1,
    limit = visaoGeralLimit,
    opts?: { placa?: string; fatura?: string; tipoVeiculo?: string; dataInicio?: string; dataFim?: string; praca?: string }
  ) => {
    const placaParam = opts?.placa !== undefined ? opts.placa : placaFiltro.trim();
    const faturaParam = opts?.fatura !== undefined ? opts.fatura : visaoGeralFaturaFiltro.trim();
    const tipoVeiculoParam = opts?.tipoVeiculo !== undefined ? opts.tipoVeiculo : visaoGeralTipoVeiculoFiltro.trim();
    const dataInicioParam = opts?.dataInicio !== undefined ? opts.dataInicio : visaoGeralDataInicio;
    const dataFimParam = opts?.dataFim !== undefined ? opts.dataFim : visaoGeralDataFim;
    const pracaParam = opts?.praca !== undefined ? opts.praca : pracaSelecionada;
    setLoadingVisaoGeral(true);
    setView('visaoGeral');
    setPracaSelecionada(pracaParam || '');
    
    // Carrega indicadores de passagens por praça
    buscarPassagensProPraca({
      placa: placaParam,
      fatura: faturaParam,
      tipoVeiculo: tipoVeiculoParam,
      dataInicio: dataInicioParam,
      dataFim: dataFimParam
    });
    
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (placaParam) params.set('placa', placaParam);
      if (faturaParam) params.set('fatura', faturaParam);
      if (tipoVeiculoParam) params.set('tipoVeiculo', tipoVeiculoParam);
      if (dataInicioParam) params.set('dataInicio', dataInicioParam);
      if (dataFimParam) params.set('dataFim', dataFimParam);
      if (pracaParam) params.set('praca', pracaParam);
      const res = await fetch(`/api/levantamento-posicoes/visao-geral?${params}`);
      if (res.ok) {
        const data = await res.json();
        setVisaoGeralRecords(data.records || []);
        setVisaoGeralTotal(data.total || 0);
        setVisaoGeralPage(page);
      } else {
        setStatusMessage('Erro ao buscar visão geral.');
      }
    } catch (error) {
      console.error('Erro ao buscar visão geral:', error);
      setStatusMessage('Erro ao buscar visão geral.');
    } finally {
      setLoadingVisaoGeral(false);
    }
  };

  const buscarPassagensProPraca = async (opts?: {
    placa?: string;
    fatura?: string;
    tipoVeiculo?: string;
    dataInicio?: string;
    dataFim?: string;
  }) => {
    const placaParam = opts?.placa !== undefined ? opts.placa : placaFiltro.trim();
    const faturaParam = opts?.fatura !== undefined ? opts.fatura : visaoGeralFaturaFiltro.trim();
    const tipoVeiculoParam = opts?.tipoVeiculo !== undefined ? opts.tipoVeiculo : visaoGeralTipoVeiculoFiltro.trim();
    const dataInicioParam = opts?.dataInicio !== undefined ? opts.dataInicio : visaoGeralDataInicio;
    const dataFimParam = opts?.dataFim !== undefined ? opts.dataFim : visaoGeralDataFim;

    setLoadingPassagensProPraca(true);
    try {
      const params = new URLSearchParams();
      if (placaParam) params.set('placa', placaParam);
      if (faturaParam) params.set('fatura', faturaParam);
      if (tipoVeiculoParam) params.set('tipoVeiculo', tipoVeiculoParam);
      if (dataInicioParam) params.set('dataInicio', dataInicioParam);
      if (dataFimParam) params.set('dataFim', dataFimParam);

      const res = await fetch(`/api/levantamento-posicoes/passagens-por-praca?${params}`);
      if (res.ok) {
        const data = await res.json();
        setPassagensProPraca(Array.isArray(data) ? data : []);
      } else {
        console.error('Erro ao buscar passagens por praça');
      }
    } catch (error) {
      console.error('Erro ao buscar passagens por praça:', error);
    } finally {
      setLoadingPassagensProPraca(false);
    }
  };

  const exportarVisaoGeralCsv = async () => {
    if (visaoGeralTotal === 0 || exportandoVisaoGeral) {
      return;
    }

    setExportandoVisaoGeral(true);

    try {
      const exportLimit = 10000;
      const totalPaginas = Math.ceil(visaoGeralTotal / exportLimit);
      const registros: (FaturaRecord & { placa_veiculo?: string })[] = [];

      for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
        const params = new URLSearchParams({
          page: String(pagina),
          limit: String(exportLimit),
          export: '1'
        });

        if (placaFiltro.trim()) params.set('placa', placaFiltro.trim());
        if (visaoGeralFaturaFiltro.trim()) params.set('fatura', visaoGeralFaturaFiltro.trim());
        if (visaoGeralTipoVeiculoFiltro.trim()) params.set('tipoVeiculo', visaoGeralTipoVeiculoFiltro.trim());
        if (visaoGeralDataInicio) params.set('dataInicio', visaoGeralDataInicio);
        if (visaoGeralDataFim) params.set('dataFim', visaoGeralDataFim);
        if (pracaSelecionada) params.set('praca', pracaSelecionada);

        const res = await fetch(`/api/levantamento-posicoes/visao-geral?${params}`);
        if (!res.ok) {
          throw new Error(`Falha ao buscar a página ${pagina} da exportação.`);
        }

        const data = await res.json();
        registros.push(...(Array.isArray(data.records) ? data.records : []));
      }

      const escaparCsv = (valor: unknown) => {
        const texto = valor === null || valor === undefined ? '' : String(valor);
        return `"${texto.replace(/"/g, '""')}"`;
      };
      const cabecalho = [
        'Nº Fatura', 'Data', 'Horário', 'Placa', 'Tipo Veículo',
        'Descrição', 'Tipo Uso', 'Valor (R$)', 'Débito/Crédito', 'Vale Pedágio'
      ];
      const linhas = registros.map(record => [
        record.numero_da_fatura,
        record.data,
        record.horario,
        record.placa_veiculo,
        record.tipo_do_veiculo,
        record.descricao,
        record.tipo_de_uso,
        record.valor_rs,
        record.debito_credito,
        record.viagem_de_vale_pedagio
      ]);
      const csv = [cabecalho, ...linhas]
        .map(linha => linha.map(escaparCsv).join(';'))
        .join('\r\n');
      const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const dataArquivo = new Date().toISOString().slice(0, 10);
      link.href = url;
      link.download = `passagens_sem_vale_pedagio_${dataArquivo}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar visão geral de passagens:', error);
      setStatusMessage('Erro ao exportar as passagens. Tente novamente.');
      window.alert('Não foi possível exportar as passagens. Tente novamente.');
    } finally {
      setExportandoVisaoGeral(false);
    }
  };

  const buscarVisaoGeralCva = async (
    page = 1,
    limit = visaoGeralCvaLimit,
    cvaTerm = visaoGeralCvaFiltro.trim(),
    dataInicio = visaoGeralCvaDataInicio,
    dataFim = visaoGeralCvaDataFim,
    statusUtilizacao = visaoGeralCvaStatusUtilizacao,
    somenteAlerta = visaoGeralCvaSomenteAlerta,
    sortBy = visaoGeralCvaSortBy,
    sortDir: 'asc' | 'desc' = visaoGeralCvaSortDir
  ) => {
    setLoadingVisaoGeralCva(true);
    setView('visaoGeralCva');

    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (cvaTerm) params.set('cva', cvaTerm);
      if (dataInicio) params.set('dataInicio', dataInicio);
      if (dataFim) params.set('dataFim', dataFim);
      if (statusUtilizacao) params.set('statusUtilizacao', statusUtilizacao);
      if (sortBy) { params.set('sortBy', sortBy); params.set('sortDir', sortDir); }

      const res = await fetch(`/api/levantamento-posicoes/visao-geral-cva?${params}`);
      if (res.ok) {
        const data = await res.json();
        const baseRecords: CvaVisaoGeralRecord[] = Array.isArray(data.records) ? data.records : [];

        const recordsComAlerta = await Promise.all(
          baseRecords.map(async (record) => {
            try {
              const alertaRes = await fetch(`/api/levantamento-posicoes/cobrancas-pracas-faltantes?cva=${encodeURIComponent(record.cva)}`);
              if (!alertaRes.ok) {
                return { ...record, tem_alerta_sem_vinculo: 0 };
              }

              const alertaData: CobrancasPracasFaltantesResponse = await alertaRes.json();
              const temAlerta = Number(alertaData?.total_valor_sem_vinculo || 0) > 0 ? 1 : 0;
              return { ...record, tem_alerta_sem_vinculo: temAlerta };
            } catch {
              return { ...record, tem_alerta_sem_vinculo: 0 };
            }
          })
        );

        const recordsExibicao = somenteAlerta
          ? recordsComAlerta.filter(item => Number(item.tem_alerta_sem_vinculo || 0) === 1)
          : recordsComAlerta;

        const resumoFiltrado = somenteAlerta
          ? {
              quantidade_cva: recordsExibicao.length,
              cupons_concedidos: recordsExibicao.reduce((acc, item) => acc + Number(item.cupons_concedidos || 0), 0),
              cupons_utilizados: recordsExibicao.reduce((acc, item) => acc + Number(item.cupons_utilizados || 0), 0),
              cupons_nao_contabilizados: recordsExibicao.reduce((acc, item) => acc + Number(item.cupons_nao_contabilizados || 0), 0),
              valor_concedido: recordsExibicao.reduce((acc, item) => acc + Number(item.valor_cva || 0), 0),
              valor_creditado: recordsExibicao.reduce((acc, item) => acc + Number(item.total_credito_concedido || 0), 0),
              diferenca_valor: recordsExibicao.reduce((acc, item) => acc + (Number(item.valor_cva || 0) - Number(item.total_credito_concedido || 0)), 0)
            }
          : null;

        setVisaoGeralCvaRecords(recordsExibicao);
        setVisaoGeralCvaTotal(somenteAlerta ? recordsExibicao.length : Number(data.total || 0));
        setVisaoGeralCvaPage(page);
        setVisaoGeralCvaResumo({
          quantidade_cva: Number(resumoFiltrado?.quantidade_cva ?? data?.resumo?.quantidade_cva ?? data.total ?? 0),
          cupons_concedidos: Number(resumoFiltrado?.cupons_concedidos ?? data?.resumo?.cupons_concedidos ?? 0),
          cupons_utilizados: Number(resumoFiltrado?.cupons_utilizados ?? data?.resumo?.cupons_utilizados ?? 0),
          cupons_nao_contabilizados: Number(resumoFiltrado?.cupons_nao_contabilizados ?? data?.resumo?.cupons_nao_contabilizados ?? 0),
          valor_concedido: Number(resumoFiltrado?.valor_concedido ?? data?.resumo?.valor_concedido ?? 0),
          valor_creditado: Number(resumoFiltrado?.valor_creditado ?? data?.resumo?.valor_creditado ?? 0),
          diferenca_valor: Number(resumoFiltrado?.diferenca_valor ?? data?.resumo?.diferenca_valor ?? 0)
        });
      } else {
        setStatusMessage('Erro ao buscar visão geral por CVA.');
      }
    } catch (error) {
      console.error('Erro ao buscar visão geral por CVA:', error);
      setStatusMessage('Erro ao buscar visão geral por CVA.');
    } finally {
      setLoadingVisaoGeralCva(false);
    }
  };

  const toggleCvaFaturaExpand = async (cva: string) => {
    if (expandedCvaKey === cva) {
      setExpandedCvaKey(null);
      setExpandedCvaFatura([]);
      setExpandedCvaPracasFaltantes([]);
      setExpandedCvaCobrancasFaltantes([]);
      setExpandedCvaTotalSemVinculo(0);
      setDetalhesSelecionados([]);
      setMostrarSomenteSelecionados(false);
      setSelecaoSalvaMensagem('');
      return;
    }
    setExpandedCvaKey(cva);
    setExpandedCvaFatura([]);
    setExpandedCvaPracasFaltantes([]);
    setExpandedCvaCobrancasFaltantes([]);
    setExpandedCvaTotalSemVinculo(0);
    setDetalhesSelecionados([]);
    setMostrarSomenteSelecionados(false);
    setSelecaoSalvaMensagem('');
    setLoadingExpandedCva(true);
    try {
      const [resFatura, resCobrancas] = await Promise.all([
        fetch(`/api/levantamento-posicoes/fatura-por-cva?cva=${encodeURIComponent(cva)}`),
        fetch(`/api/levantamento-posicoes/cobrancas-pracas-faltantes?cva=${encodeURIComponent(cva)}`)
      ]);

      if (resFatura.ok) {
        const dataFatura = await resFatura.json();
        setExpandedCvaFatura(Array.isArray(dataFatura) ? dataFatura : []);
      }

      if (resCobrancas.ok) {
        const dataCobrancas: CobrancasPracasFaltantesResponse = await resCobrancas.json();
        setExpandedCvaPracasFaltantes(Array.isArray(dataCobrancas?.pracas_faltantes) ? dataCobrancas.pracas_faltantes : []);
        const cobrancasSemVinculo = Array.isArray(dataCobrancas?.cobrancas_sem_vinculo) ? dataCobrancas.cobrancas_sem_vinculo : [];
        setExpandedCvaCobrancasFaltantes(cobrancasSemVinculo);
        setExpandedCvaTotalSemVinculo(Number(dataCobrancas?.total_valor_sem_vinculo || 0));
        carregarSelecaoSalva(cva, cobrancasSemVinculo);
      }
    } catch (err) {
      console.error('Erro ao buscar fatura do CVA:', err);
    } finally {
      setLoadingExpandedCva(false);
    }
  };

  const resumoPracasFaltantes = useMemo(() => {
    const mapCobrancas = new Map(
      expandedCvaCobrancasFaltantes.map(item => [item.praca, item])
    );

    return expandedCvaPracasFaltantes.map((praca) => {
      const cobranca = mapCobrancas.get(praca);
      return {
        praca,
        quantidade: Number(cobranca?.quantidade || 0),
        valor_total: Number(cobranca?.valor_total || 0)
      };
    });
  }, [expandedCvaPracasFaltantes, expandedCvaCobrancasFaltantes]);

  const detalhesCobrancasSemVinculo = useMemo(() => {
    const detalhes: Array<CobrancaPracaFaltanteRegistro & { praca: string }> = [];

    expandedCvaCobrancasFaltantes.forEach((item) => {
      (item.registros || []).forEach((registro) => {
        detalhes.push({
          ...registro,
          praca: item.praca
        });
      });
    });

    return detalhes;
  }, [expandedCvaCobrancasFaltantes]);

  const getDetalheKey = (item: CobrancaPracaFaltanteRegistro & { praca: string }): string => {
    return [
      item.praca,
      item.numero_da_fatura,
      item.data,
      item.horario,
      item.valor_rs,
      item.tipo_de_uso,
      item.debito_credito
    ].join('|');
  };

  const getSelecaoStorageKey = (cva: string): string => `lp-cva-selecao-detalhes-${cva}`;

  const carregarSelecaoSalva = (cva: string, cobrancas: CobrancaPracaFaltante[]) => {
    try {
      const storageKey = getSelecaoStorageKey(cva);
      const raw = localStorage.getItem(storageKey);
      const saved: string[] = raw ? JSON.parse(raw) : [];

      if (!Array.isArray(saved) || saved.length === 0) {
        setDetalhesSelecionados([]);
        setMostrarSomenteSelecionados(false);
        return;
      }

      const disponiveis = new Set<string>();
      cobrancas.forEach((item) => {
        (item.registros || []).forEach((registro) => {
          disponiveis.add(getDetalheKey({ ...registro, praca: item.praca }));
        });
      });

      const validos = saved.filter((key) => disponiveis.has(key));
      setDetalhesSelecionados(validos);
      setMostrarSomenteSelecionados(validos.length > 0);
    } catch {
      setDetalhesSelecionados([]);
      setMostrarSomenteSelecionados(false);
    }
  };

  const salvarSelecaoAtual = () => {
    if (!expandedCvaKey) {
      return;
    }

    try {
      const storageKey = getSelecaoStorageKey(expandedCvaKey);
      if (detalhesSelecionados.length === 0) {
        localStorage.removeItem(storageKey);
        setMostrarSomenteSelecionados(false);
        setSelecaoSalvaMensagem('Selecao limpa para este CVA.');
      } else {
        localStorage.setItem(storageKey, JSON.stringify(detalhesSelecionados));
        setMostrarSomenteSelecionados(true);
        setSelecaoSalvaMensagem(`Selecao salva (${detalhesSelecionados.length} linha(s)).`);
      }

      window.setTimeout(() => setSelecaoSalvaMensagem(''), 2200);
    } catch {
      setSelecaoSalvaMensagem('Nao foi possivel salvar a selecao.');
      window.setTimeout(() => setSelecaoSalvaMensagem(''), 2200);
    }
  };

  const detalhesCobrancasVisiveis = useMemo(() => {
    if (!mostrarSomenteSelecionados) {
      return detalhesCobrancasSemVinculo;
    }

    const selectedSet = new Set(detalhesSelecionados);
    return detalhesCobrancasSemVinculo.filter(item => selectedSet.has(getDetalheKey(item)));
  }, [detalhesCobrancasSemVinculo, detalhesSelecionados, mostrarSomenteSelecionados]);

  const exportarVisaoGeralCvaCsv = async () => {
    if (visaoGeralCvaTotal === 0 && visaoGeralCvaRecords.length === 0) {
      setStatusMessage('Não há dados na tabela para exportar.');
      return;
    }

    setExportandoVisaoGeralCva(true);
    setStatusMessage('Exportando todos os registros filtrados...');

    try {
      const exportLimit = 500;
      const totalRegistros = visaoGeralCvaTotal || visaoGeralCvaRecords.length;
      const totalPaginas = Math.max(1, Math.ceil(totalRegistros / exportLimit));
      const registrosExportacao: CvaVisaoGeralRecord[] = [];

      for (let pagina = 1; pagina <= totalPaginas; pagina += 1) {
        const params = new URLSearchParams({ page: String(pagina), limit: String(exportLimit) });
        const cvaTerm = visaoGeralCvaFiltro.trim();

        if (cvaTerm) params.set('cva', cvaTerm);
        if (visaoGeralCvaDataInicio) params.set('dataInicio', visaoGeralCvaDataInicio);
        if (visaoGeralCvaDataFim) params.set('dataFim', visaoGeralCvaDataFim);
        if (visaoGeralCvaStatusUtilizacao) {
          params.set('statusUtilizacao', visaoGeralCvaStatusUtilizacao);
        }

        const res = await fetch(`/api/levantamento-posicoes/visao-geral-cva?${params}`);
        if (!res.ok) {
          throw new Error(`Falha ao buscar página ${pagina} para exportação.`);
        }

        const data = await res.json();
        const registrosPagina = Array.isArray(data.records) ? data.records : [];
        registrosExportacao.push(...registrosPagina);
      }

      const headers = [
        'CVA',
        'Vale Pedágio',
        'Data Pedido',
        'Placa',
        'Cupons Concedidos',
        'Cupons Utilizados (CR)',
        'Cupons Não Contabilizados',
        'Valor do CVA',
        'Total Crédito na Fatura',
        'Diferença'
      ];

      const rows = registrosExportacao.map(record => [
        summaryFieldValue(record.cva),
        summaryFieldValue(record.vale_pedagio),
        summaryFieldValue(record.data_pedido),
        summaryFieldValue(record.placa_do_veiculo),
        summaryFieldValue(record.cupons_concedidos),
        summaryFieldValue(record.cupons_utilizados),
        summaryFieldValue(record.cupons_nao_contabilizados),
        formatCurrencyValue(record.valor_cva),
        formatCurrencyValue(record.total_credito_concedido),
        formatCurrencyValue(calculateCurrencyDifference(record.valor_cva, record.total_credito_concedido))
      ]);

      const csvContent = [headers, ...rows]
        .map(row => row.map(cell => escapeCsvCell(cell)).join(';'))
        .join('\r\n');

      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const statusPart = visaoGeralCvaStatusUtilizacao ? `_${sanitizeFilePart(visaoGeralCvaStatusUtilizacao)}` : '';
      const cvaPart = visaoGeralCvaFiltro.trim() ? `_${sanitizeFilePart(visaoGeralCvaFiltro.trim())}` : '';
      const inicioPart = visaoGeralCvaDataInicio ? `_${sanitizeFilePart(visaoGeralCvaDataInicio)}` : '';
      const fimPart = visaoGeralCvaDataFim ? `_${sanitizeFilePart(visaoGeralCvaDataFim)}` : '';

      link.href = url;
      link.download = `visao_geral_cva${statusPart}${cvaPart}${inicioPart}${fimPart}_completo.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setStatusMessage(`${registrosExportacao.length.toLocaleString('pt-BR')} registro(s) exportado(s) com sucesso.`);
    } catch (error) {
      console.error('Erro ao exportar tabela de visão geral por CVA:', error);
      setStatusMessage('Erro ao exportar tabela de visão geral por CVA.');
    } finally {
      setExportandoVisaoGeralCva(false);
    }
  };

  const alternarFiltroPraca = (praca: string) => {
    const proximoFiltro = pracaSelecionada === praca ? '' : praca;

    buscarVisaoGeral(1, visaoGeralLimit, {
      placa: placaFiltro.trim(),
      fatura: visaoGeralFaturaFiltro.trim(),
      tipoVeiculo: visaoGeralTipoVeiculoFiltro.trim(),
      dataInicio: visaoGeralDataInicio,
      dataFim: visaoGeralDataFim,
      praca: proximoFiltro
    });
  };

  const valorTotalGeralPracas = useMemo(() => {
    return passagensProPraca.reduce((acc, item) => {
      if (item.praca === 'Outras Praças') return acc;
      return acc + Number(item.valorTotal || 0);
    }, 0);
  }, [passagensProPraca]);

  const totalPassagensGeralPracas = useMemo(() => {
    return passagensProPraca.reduce((acc, item) => {
      if (item.praca === 'Outras Praças') return acc;
      return acc + Number(item.total || 0);
    }, 0);
  }, [passagensProPraca]);

  const pontosFiltrados = useMemo(() => {
    if (placaSelecionada === 'TODAS') {
      return allPoints;
    }

    return allPoints.filter(point => point.placa === placaSelecionada);
  }, [allPoints, placaSelecionada]);

  const coordinates = useMemo<Array<[number, number]>>(
    () => pontosFiltrados.map(point => [point.latitude, point.longitude]),
    [pontosFiltrados]
  );

  useEffect(() => {
    let active = true;
    const abortController = new AbortController();

    const buildRoadPolyline = async () => {
      if (pontosFiltrados.length < 2) {
        if (active) {
          setRoadPolylines([]);
          setRoadStatus('');
        }
        return;
      }

      if (active) {
        setRoadStatus('Ajustando rota para seguir a estrada...');
      }

      try {
        const segments = splitPointsIntoSegments(pontosFiltrados);
        if (segments.length === 0) {
          setRoadPolylines([]);
          setRoadStatus('Sem segmentos válidos para traçar rota.');
          return;
        }

        const routedSegments: Array<Array<[number, number]>> = [];
        let fallbackCount = 0;

        for (const segment of segments) {
          try {
            const routed = await fetchRoadRoutedPolyline(segment, abortController.signal);
            if (routed.length >= 2) {
              routedSegments.push(routed);
            }
          } catch (segmentError) {
            console.warn('Falha ao ajustar segmento por estrada; usando segmento aproximado:', segmentError);
            fallbackCount += 1;

            const fallbackSegment = sampleCoordinates(segment, 80);
            if (fallbackSegment.length >= 2) {
              routedSegments.push(fallbackSegment);
            }
          }
        }

        if (!active) {
          return;
        }

        setRoadPolylines(routedSegments);
        if (routedSegments.length === 0) {
          setRoadStatus('Não foi possível montar rota viária para este período.');
        } else if (fallbackCount === 0) {
          setRoadStatus('Rota ajustada pela malha viária.');
        } else {
          setRoadStatus(`Rota viária ajustada com ${fallbackCount} segmento(s) aproximado(s).`);
        }
      } catch (error) {
        if (!active) {
          return;
        }

        console.warn('Falha ao ajustar rota por estrada:', error);
        setRoadPolylines([]);
        setRoadStatus('Não foi possível ajustar pela estrada; exibindo rota provisória.');
      }
    };

    buildRoadPolyline();

    return () => {
      active = false;
      abortController.abort();
    };
  }, [pontosFiltrados]);

  const displaySegments = useMemo(() => {
    if (roadPolylines.length > 0) {
      return bridgeNearbySegmentGaps(roadPolylines, 3);
    }

    // Enquanto ajusta por estrada, mostra uma prévia para evitar mapa vazio.
    if (coordinates.length >= 2) {
      return [sampleCoordinates(coordinates, 280)];
    }

    return [];
  }, [roadPolylines, coordinates]);

  const displayCoordinates = useMemo(() => flattenSegments(displaySegments), [displaySegments]);

  const totalDistanceKm = useMemo(() => {
    if (pontosFiltrados.length < 2) {
      return 0;
    }

    let total = 0;

    for (let i = 1; i < pontosFiltrados.length; i += 1) {
      total += haversineKm(pontosFiltrados[i - 1], pontosFiltrados[i]);
    }

    return total;
  }, [pontosFiltrados]);

  const [firstPoint, lastPoint] = useMemo(() => {
    if (pontosFiltrados.length === 0) {
      return [null, null] as const;
    }

    return [pontosFiltrados[0], pontosFiltrados[pontosFiltrados.length - 1]] as const;
  }, [pontosFiltrados]);

  const tripDuration = useMemo(() => {
    if (!firstPoint?.timestamp || !lastPoint?.timestamp) {
      return '-';
    }

    return toReadableDuration(lastPoint.timestamp - firstPoint.timestamp);
  }, [firstPoint, lastPoint]);

  const averageSpeed = useMemo(() => {
    const withSpeed = pontosFiltrados.filter(point => point.velocidade !== null);
    if (withSpeed.length === 0) {
      return null;
    }

    const sum = withSpeed.reduce((acc, current) => acc + (current.velocidade || 0), 0);
    return sum / withSpeed.length;
  }, [pontosFiltrados]);

  const exportarPdf = async () => {
    if (pontosFiltrados.length === 0) {
      setStatusMessage('Não há dados para exportar. Faça uma consulta primeiro.');
      return;
    }

    setExportingPdf(true);

    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 12;
      let cursorY = 12;

      const placaDoc = placaSelecionada !== 'TODAS'
        ? placaSelecionada
        : (placaFiltro.trim().toUpperCase() || 'TODAS');

      // Cabeçalho no estilo do modelo: faixa azul e curva branca assimétrica.
      doc.setFillColor(60, 99, 174);
      doc.rect(0, 0, pageWidth, 18, 'F');

      doc.setFillColor(255, 255, 255);
      doc.ellipse(pageWidth * 0.20, 24.5, pageWidth * 0.88, 8.4, 'F');

      const logoDataUrl =
        (await fetchImageAsDataUrl('/logo-gestor.png')) ||
        (await fetchImageAsDataUrl('/logo-fks.png'));
      if (logoDataUrl) {
        try {
          doc.addImage(logoDataUrl, 'PNG', margin - 1, 6, 34, 8.8);
        } catch (error) {
          console.warn('Falha ao inserir logo no PDF:', error);
        }
      }

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.2);
      doc.text('Posicoes do veiculo', pageWidth - margin, 7.2, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.8);
      doc.text(new Date().toLocaleString('pt-BR'), pageWidth - margin, 12.1, { align: 'right' });

      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'normal');
      cursorY = 36;

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Relatorio de Percurso', margin, cursorY);
      cursorY += 6;

      doc.setDrawColor(210, 210, 210);
      doc.line(margin, cursorY, pageWidth - margin, cursorY);
      cursorY += 8;

      // Bloco de metadados
      doc.setDrawColor(180, 180, 180);
      doc.roundedRect(margin, cursorY - 4, pageWidth - margin * 2, 54, 2, 2);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Placa:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(placaDoc, margin + 26, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Pontos exibidos:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(String(pontosFiltrados.length), margin + 40, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Distancia estimada:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(`${totalDistanceKm.toFixed(2)} km`, margin + 40, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Inicio:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDateTime(firstPoint?.timestamp || null), margin + 26, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Fim:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(formatDateTime(lastPoint?.timestamp || null), margin + 26, cursorY + 3);
      cursorY += 8;

      doc.setFont('helvetica', 'bold');
      doc.text('Duracao:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(tripDuration, margin + 26, cursorY + 3);
      cursorY += 10;

      // Resumo automático por CVA para auditoria de cupons.
      const resumoBoxHeight = 36;
      doc.setDrawColor(180, 180, 180);
      doc.roundedRect(margin, cursorY - 4, pageWidth - margin * 2, resumoBoxHeight, 2, 2);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Resumo de Cupons por CVA', margin + 3, cursorY + 2);
      cursorY += 7;

      doc.setFont('helvetica', 'bold');
      doc.text('CVA:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.cva || cvaFiltro), margin + 24, cursorY + 3);

      doc.setFont('helvetica', 'bold');
      doc.text('Vale Pedagio:', margin + 72, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.vale_pedagio), margin + 103, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Data Pedido:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.data_pedido), margin + 31, cursorY + 3);

      doc.setFont('helvetica', 'bold');
      doc.text('Cupons Concedidos:', margin + 72, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.cupons_concedidos), margin + 118, cursorY + 3);
      cursorY += 6;

      doc.setFont('helvetica', 'bold');
      doc.text('Cupons Utilizados:', margin + 3, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.cupons_utilizados), margin + 44, cursorY + 3);

      doc.setFont('helvetica', 'bold');
      doc.text('Nao Contabilizados:', margin + 72, cursorY + 3);
      doc.setFont('helvetica', 'normal');
      doc.text(summaryFieldValue(resumoCva?.cupons_nao_contabilizados), margin + 118, cursorY + 3);
      cursorY += 4;

      const mapElement = mapExportRef.current;
      if (mapElement) {
        try {
          const leafletContainer =
            mapElement.querySelector('.leaflet-container') as HTMLElement | null;
          const targetElement = leafletContainer || mapElement;

          // Exporta exatamente o que está visível no mapa, sem reposicionar.
          await wait(250);
          await waitForLeafletTiles(targetElement, 3500);

          const canvas = await captureMapCanvas(targetElement);

          const imageData = canvas.toDataURL('image/jpeg', 0.92);

          // Página 2 (landscape) só para o mapa, evitando corte.
          doc.addPage('a4', 'landscape');
          const mapPageWidth = doc.internal.pageSize.getWidth();
          const mapPageHeight = doc.internal.pageSize.getHeight();
          const mapMargin = 10;

          doc.setFont('helvetica', 'bold');
          doc.setFontSize(12);
          doc.text('Percurso no Mapa', mapMargin, 12);

          const drawAreaTop = 16;
          const drawWidth = mapPageWidth - mapMargin * 2;
          const drawHeight = mapPageHeight - drawAreaTop - mapMargin;

          const imageRatio = canvas.width / canvas.height;
          let imageWidth = drawWidth;
          let imageHeight = imageWidth / imageRatio;

          if (imageHeight > drawHeight) {
            imageHeight = drawHeight;
            imageWidth = imageHeight * imageRatio;
          }

          const imageX = mapMargin + (drawWidth - imageWidth) / 2;
          const imageY = drawAreaTop;

          doc.addImage(imageData, 'JPEG', imageX, imageY, imageWidth, imageHeight);
        } catch (mapError) {
          console.warn('Falha ao capturar mapa para PDF:', mapError);
          doc.setFontSize(10);
          doc.text('Nao foi possivel capturar o mapa neste ambiente.', margin, cursorY);
          cursorY += 6;
        }
      }

      doc.setPage(1);
      doc.setFontSize(9);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, pageHeight - 10);

      const fileName = [
        'documentacao_levantamento_posicoes',
        sanitizeFilePart(placaDoc),
        sanitizeFilePart(cvaFiltro.trim() || '')
      ].filter(Boolean).join('_') + '.pdf';

      doc.save(fileName);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      setStatusMessage('Erro ao gerar PDF. Tente novamente.');
    } finally {
      setExportingPdf(false);
    }
  };

  const importarExcel = async () => {
    if (selectedExcelFiles.length === 0) {
      setStatusMessage('Selecione ao menos um arquivo Excel para importar.');
      return;
    }

    setImportingExcel(true);
    setStatusMessage(`Importando ${selectedExcelFiles.length} planilha(s) de viagens...`);

    try {
      for (let i = 0; i < selectedExcelFiles.length; i += 1) {
        const file = selectedExcelFiles[i];
        const formData = new FormData();
        formData.append('arquivo', file);
        setStatusMessage(`Importando viagens (${i + 1}/${selectedExcelFiles.length}): ${file.name}`);

        const res = await fetch('/api/levantamento-posicoes/importar-excel', {
          method: 'POST',
          body: formData
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setStatusMessage(data.message || `Erro ao importar planilha: ${file.name}`);
          return;
        }
      }

      setStatusMessage(`${selectedExcelFiles.length} planilha(s) de viagens importada(s) com sucesso.`);
      setSelectedExcelFiles([]);
      if (excelInputRef.current) {
        excelInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao importar Excel:', error);
      setStatusMessage('Erro ao importar planilha.');
    } finally {
      setImportingExcel(false);
    }
  };

  const importarFaturaExcel = async () => {
    if (selectedFaturaExcelFiles.length === 0) {
      setStatusMessage('Selecione ao menos um arquivo Excel de fatura para importar.');
      return;
    }

    setImportingFaturaExcel(true);
    setStatusMessage(`Importando ${selectedFaturaExcelFiles.length} planilha(s) de fatura...`);

    try {
      for (let i = 0; i < selectedFaturaExcelFiles.length; i += 1) {
        const file = selectedFaturaExcelFiles[i];
        const formData = new FormData();
        formData.append('arquivo', file);
        setStatusMessage(`Importando fatura (${i + 1}/${selectedFaturaExcelFiles.length}): ${file.name}`);

        const res = await fetch('/api/levantamento-posicoes/importar-fatura-excel', {
          method: 'POST',
          body: formData
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          setStatusMessage(data.message || `Erro ao importar planilha de fatura: ${file.name}`);
          return;
        }
      }

      setStatusMessage(`${selectedFaturaExcelFiles.length} planilha(s) de fatura importada(s) com sucesso.`);
      setSelectedFaturaExcelFiles([]);
      if (faturaExcelInputRef.current) {
        faturaExcelInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Erro ao importar Excel de fatura:', error);
      setStatusMessage('Erro ao importar planilha de fatura.');
    } finally {
      setImportingFaturaExcel(false);
    }
  };

  if (view === 'visaoGeralCva') {
    return (
      <div className="levantamento-posicoes-module">
        <header className="lp-header lp-header-visao-geral">
          <div className="lp-header-content">
            <h2>Visão Geral por CVA</h2>
            <p>Resumo de cupons por CVA</p>
          </div>
          <button className="lp-btn-back" type="button" onClick={() => setView('principal')}>
            ←
          </button>
        </header>

        <section className="lp-fatura-table-section">
          <div className="lp-vg-filter-card">
            <p className="lp-vg-filter-title">Filtros</p>
            <div className="lp-vg-filter-grid">
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-cva">CVA</label>
                <input
                  id="lp-vg-cva"
                  type="text"
                  value={visaoGeralCvaFiltro}
                  onChange={event => setVisaoGeralCvaFiltro(event.target.value)}
                  placeholder="Ex: 123456"
                />
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-cva-status">Utilização</label>
                <select
                  id="lp-vg-cva-status"
                  value={visaoGeralCvaStatusUtilizacao}
                  onChange={event => setVisaoGeralCvaStatusUtilizacao(event.target.value)}
                >
                  <option value="">Todos</option>
                  <option value="completo">Utilizado Completo</option>
                  <option value="incompleto">Utilizado Incompleto</option>
                  <option value="nunca-utilizado">Nunca Utilizado</option>
                </select>
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-cva-data-inicio">Data Início</label>
                <input
                  id="lp-vg-cva-data-inicio"
                  type="date"
                  value={visaoGeralCvaDataInicio}
                  onChange={event => setVisaoGeralCvaDataInicio(event.target.value)}
                />
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-cva-data-fim">Data Fim</label>
                <input
                  id="lp-vg-cva-data-fim"
                  type="date"
                  value={visaoGeralCvaDataFim}
                  onChange={event => setVisaoGeralCvaDataFim(event.target.value)}
                />
              </div>
              <div className="lp-vg-filter-field lp-vg-filter-checkbox">
                <label htmlFor="lp-vg-cva-alerta">Casos Prioritários</label>
                <label className="lp-checkbox-inline" htmlFor="lp-vg-cva-alerta">
                  <input
                    id="lp-vg-cva-alerta"
                    type="checkbox"
                    checked={visaoGeralCvaSomenteAlerta}
                    onChange={event => setVisaoGeralCvaSomenteAlerta(event.target.checked)}
                  />
                  Somente linhas com !
                </label>
              </div>
            </div>

            <div className="lp-vg-filter-actions">
              <button
                className="lp-btn"
                type="button"
                onClick={() => buscarVisaoGeralCva(1, visaoGeralCvaLimit, visaoGeralCvaFiltro.trim(), visaoGeralCvaDataInicio, visaoGeralCvaDataFim, visaoGeralCvaStatusUtilizacao, visaoGeralCvaSomenteAlerta)}
                disabled={loadingVisaoGeralCva}
              >
                {loadingVisaoGeralCva ? 'Filtrando...' : 'Filtrar'}
              </button>
              <button
                className="lp-btn lp-btn-muted"
                type="button"
                onClick={() => {
                  setVisaoGeralCvaFiltro('');
                  setVisaoGeralCvaDataInicio('');
                  setVisaoGeralCvaDataFim('');
                  setVisaoGeralCvaStatusUtilizacao('');
                  setVisaoGeralCvaSomenteAlerta(false);
                  buscarVisaoGeralCva(1, visaoGeralCvaLimit, '', '', '', '', false);
                }}
                disabled={loadingVisaoGeralCva}
              >
                Limpar
              </button>
              <button
                className="lp-btn"
                type="button"
                onClick={exportarVisaoGeralCvaCsv}
                disabled={loadingVisaoGeralCva || exportandoVisaoGeralCva || visaoGeralCvaRecords.length === 0}
              >
                {exportandoVisaoGeralCva ? 'Exportando...' : 'Exportar CSV'}
              </button>
              <span className="lp-vg-result-badge">
                {visaoGeralCvaTotal.toLocaleString('pt-BR')} registro{visaoGeralCvaTotal !== 1 ? 's' : ''} encontrado{visaoGeralCvaTotal !== 1 ? 's' : ''}
              </span>
              <div className="lp-page-size-selector">
                <label htmlFor="lp-page-size-vg-cva">Exibir:</label>
                <select
                  id="lp-page-size-vg-cva"
                  value={visaoGeralCvaLimit}
                  onChange={event => {
                    const newLimit = Number(event.target.value);
                    setVisaoGeralCvaLimit(newLimit);
                    buscarVisaoGeralCva(1, newLimit, visaoGeralCvaFiltro.trim(), visaoGeralCvaDataInicio, visaoGeralCvaDataFim, visaoGeralCvaStatusUtilizacao, visaoGeralCvaSomenteAlerta);
                  }}
                >
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                  <option value={200}>200 por página</option>
                  <option value={500}>500 por página</option>
                </select>
              </div>
            </div>
          </div>

          <div className="lp-cva-kpi-grid">
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Quantidade CVA</p>
              <strong className="lp-cva-kpi-value">{formatWholeNumber(visaoGeralCvaResumo.quantidade_cva)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Cupons Concedidos</p>
              <strong className="lp-cva-kpi-value">{formatWholeNumber(visaoGeralCvaResumo.cupons_concedidos)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Cupons Utilizados</p>
              <strong className="lp-cva-kpi-value">{formatWholeNumber(visaoGeralCvaResumo.cupons_utilizados)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Cupons Não Contab.</p>
              <strong className="lp-cva-kpi-value">{formatWholeNumber(visaoGeralCvaResumo.cupons_nao_contabilizados)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Valor Concedido</p>
              <strong className="lp-cva-kpi-value">{formatCurrencyValue(visaoGeralCvaResumo.valor_concedido)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Valor Creditado</p>
              <strong className="lp-cva-kpi-value">{formatCurrencyValue(visaoGeralCvaResumo.valor_creditado)}</strong>
            </div>
            <div className="lp-cva-kpi-card">
              <p className="lp-cva-kpi-label">Diferença</p>
              <strong className="lp-cva-kpi-value">{formatCurrencyValue(visaoGeralCvaResumo.diferenca_valor)}</strong>
            </div>
          </div>

          {visaoGeralCvaRecords.length === 0 ? (
            <p className="lp-no-data">
              {loadingVisaoGeralCva ? 'Carregando...' : 'Nenhum CVA encontrado.'}
            </p>
          ) : (
            <>
              <div className="lp-table-wrapper">
                <table className="lp-fatura-table lp-fatura-table-centered">
                  <thead>
                    <tr>
                      {(([
                        { key: 'cva', label: 'CVA' },
                        { key: 'vale_pedagio', label: 'Vale Pedágio' },
                        { key: 'data_pedido', label: 'Data Pedido' },
                        { key: 'placa_do_veiculo', label: 'Placa' },
                        { key: 'cupons_concedidos', label: 'Cupons Concedidos' },
                        { key: 'cupons_utilizados', label: 'Cupons Utilizados (CR)' },
                        { key: 'cupons_nao_contabilizados', label: 'Cupons Não Contabilizados' },
                        { key: 'valor_cva', label: 'Valor do CVA' },
                        { key: 'total_credito_concedido', label: 'Total Crédito na Fatura' },
                        { key: 'diferenca', label: 'Diferença' },
                      ]) as { key: string; label: string }[]).map(col => {
                        const isActive = visaoGeralCvaSortBy === col.key;
                        const nextDir: 'asc' | 'desc' = isActive && visaoGeralCvaSortDir === 'asc' ? 'desc' : 'asc';
                        return (
                          <th
                            key={col.key}
                            className={`lp-th-sortable${isActive ? ` lp-th-sort-${visaoGeralCvaSortDir}` : ''}`}
                            onClick={() => {
                              setVisaoGeralCvaSortBy(col.key);
                              setVisaoGeralCvaSortDir(nextDir);
                              buscarVisaoGeralCva(1, visaoGeralCvaLimit, visaoGeralCvaFiltro.trim(), visaoGeralCvaDataInicio, visaoGeralCvaDataFim, visaoGeralCvaStatusUtilizacao, visaoGeralCvaSomenteAlerta, col.key, nextDir);
                            }}
                          >
                            {col.label}
                            <span className="lp-sort-icon">
                              {isActive ? (visaoGeralCvaSortDir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}
                            </span>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {visaoGeralCvaRecords.map((record, idx) => {
                      const isExpanded = expandedCvaKey === record.cva;
                      return (
                        <React.Fragment key={idx}>
                          <tr
                            className={`lp-cva-row-expandable${isExpanded ? ' lp-cva-row-expanded' : ''}`}
                            onClick={() => toggleCvaFaturaExpand(record.cva)}
                            title="Clique para ver os dados da fatura"
                          >
                            <td>
                              {Number(record.tem_alerta_sem_vinculo || 0) === 1 && (
                                <span className="lp-cva-alert-badge" title="Existe cobrança sem vínculo após data do pedido em praça faltante">!</span>
                              )}
                              <span className="lp-cva-expand-icon">{isExpanded ? '▼' : '▶'}</span>
                              {summaryFieldValue(record.cva)}
                            </td>
                            <td>{summaryFieldValue(record.vale_pedagio)}</td>
                            <td>{summaryFieldValue(record.data_pedido)}</td>
                            <td>{summaryFieldValue(record.placa_do_veiculo)}</td>
                            <td>{summaryFieldValue(record.cupons_concedidos)}</td>
                            <td>{summaryFieldValue(record.cupons_utilizados)}</td>
                            <td>{summaryFieldValue(record.cupons_nao_contabilizados)}</td>
                            <td className="lp-valor-cell">{formatCurrencyValue(record.valor_cva)}</td>
                            <td className="lp-valor-cell">{formatCurrencyValue(record.total_credito_concedido)}</td>
                            <td className="lp-valor-cell">{formatCurrencyValue(calculateCurrencyDifference(record.valor_cva, record.total_credito_concedido))}</td>
                          </tr>
                          {isExpanded && (
                            <tr className="lp-cva-expand-detail-row">
                              <td colSpan={10}>
                                <div className="lp-cva-expand-panel">
                                  <div className="lp-cva-expand-panel-header">
                                    Registros da Fatura — Vale Pedágio <strong>{record.vale_pedagio}</strong>
                                  </div>
                                  {loadingExpandedCva ? (
                                    <div className="lp-cva-expand-loading">Carregando...</div>
                                  ) : expandedCvaFatura.length === 0 ? (
                                    <div className="lp-cva-expand-empty">Nenhum registro encontrado na fatura.</div>
                                  ) : (
                                    <>
                                      <div className="lp-cva-missing-pracas-box">
                                        <div className="lp-cva-missing-pracas-title">
                                          Praças Principais Faltantes ({expandedCvaPracasFaltantes.length})
                                        </div>
                                        {resumoPracasFaltantes.length === 0 ? (
                                          <div className="lp-cva-missing-pracas-empty">Nenhuma praça principal pendente para este CVA.</div>
                                        ) : (
                                          <table className="lp-cva-missing-pracas-table">
                                            <thead>
                                              <tr>
                                                <th>Praça</th>
                                                <th>Placa</th>
                                                <th>Total de Passagens</th>
                                                <th>Valor Cobrado (R$)</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {resumoPracasFaltantes.map((item, index) => (
                                                <tr key={`${item.praca}-${index}`}>
                                                  <td>{item.praca}</td>
                                                  <td>{summaryFieldValue(record.placa_do_veiculo)}</td>
                                                  <td>{summaryFieldValue(item.quantidade)}</td>
                                                  <td className="lp-valor-cell">{formatCurrencyValue(item.valor_total)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        )}
                                      </div>

                                      <div className="lp-cva-missing-charges-box">
                                        <div className="lp-cva-missing-pracas-title">
                                          Cobranças Sem Vínculo de CVA (após data do pedido)
                                        </div>
                                        {detalhesCobrancasSemVinculo.length === 0 ? (
                                          <div className="lp-cva-missing-pracas-empty">Nenhuma cobrança sem vínculo encontrada para as praças faltantes no período.</div>
                                        ) : (
                                          <>
                                            <div className="lp-cva-missing-charges-actions">
                                              <label className="lp-checkbox-inline" htmlFor="lp-mostrar-somente-selecionadas">
                                                <input
                                                  id="lp-mostrar-somente-selecionadas"
                                                  type="checkbox"
                                                  checked={mostrarSomenteSelecionados}
                                                  onChange={(event) => setMostrarSomenteSelecionados(event.target.checked)}
                                                />
                                                Mostrar somente linhas selecionadas
                                              </label>
                                              <button
                                                className="lp-btn lp-btn-small"
                                                type="button"
                                                onClick={salvarSelecaoAtual}
                                              >
                                                Salvar seleção
                                              </button>
                                              <button
                                                className="lp-btn lp-btn-muted lp-btn-small"
                                                type="button"
                                                onClick={() => {
                                                  const keys = detalhesCobrancasSemVinculo.map(item => getDetalheKey(item));
                                                  setDetalhesSelecionados(keys);
                                                }}
                                              >
                                                Selecionar todas
                                              </button>
                                              <button
                                                className="lp-btn lp-btn-muted lp-btn-small"
                                                type="button"
                                                onClick={() => {
                                                  setDetalhesSelecionados([]);
                                                  setMostrarSomenteSelecionados(false);
                                                }}
                                              >
                                                Limpar seleção
                                              </button>
                                              {selecaoSalvaMensagem && (
                                                <span className="lp-selecao-salva-msg">{selecaoSalvaMensagem}</span>
                                              )}
                                            </div>

                                            <table className="lp-cva-missing-charges-detail-table">
                                              <thead>
                                                <tr>
                                                  <th>Selecionar</th>
                                                  <th>Praça</th>
                                                  <th>Placa</th>
                                                  <th>Nº Fatura</th>
                                                  <th>Data</th>
                                                  <th>Horário</th>
                                                  <th>Tipo de Uso</th>
                                                  <th>D/C</th>
                                                  <th>Valor Cobrado (R$)</th>
                                                </tr>
                                              </thead>
                                              <tbody>
                                                {detalhesCobrancasVisiveis.map((item, idx) => {
                                                  const key = getDetalheKey(item);
                                                  const isChecked = detalhesSelecionados.includes(key);

                                                  return (
                                                  <tr key={`${item.praca}-${item.numero_da_fatura}-${item.data}-${idx}`}>
                                                    <td>
                                                      <input
                                                        type="checkbox"
                                                        checked={isChecked}
                                                        onChange={(event) => {
                                                          if (event.target.checked) {
                                                            setDetalhesSelecionados(prev => prev.includes(key) ? prev : [...prev, key]);
                                                          } else {
                                                            setDetalhesSelecionados(prev => prev.filter(itemKey => itemKey !== key));
                                                          }
                                                        }}
                                                      />
                                                    </td>
                                                    <td>{item.praca}</td>
                                                    <td>{summaryFieldValue(item.placa_veiculo || record.placa_do_veiculo)}</td>
                                                    <td>{summaryFieldValue(item.numero_da_fatura)}</td>
                                                    <td>{summaryFieldValue(item.data)}</td>
                                                    <td>{summaryFieldValue(item.horario)}</td>
                                                    <td>{summaryFieldValue(item.tipo_de_uso)}</td>
                                                    <td>{summaryFieldValue(item.debito_credito)}</td>
                                                    <td className="lp-valor-cell">{formatCurrencyValue(item.valor_rs)}</td>
                                                  </tr>
                                                  );
                                                })}
                                              </tbody>
                                            </table>
                                            <div className="lp-cva-missing-charges-total">
                                              Total sem vínculo: <strong>{formatCurrencyValue(expandedCvaTotalSemVinculo)}</strong>
                                            </div>
                                          </>
                                        )}
                                      </div>

                                      <table className="lp-cva-expand-table">
                                        <thead>
                                          <tr>
                                            <th>Nº Fatura</th>
                                            <th>Data</th>
                                            <th>Horário</th>
                                            <th>Placa</th>
                                            <th>Tipo Veículo</th>
                                            <th>Descrição</th>
                                            <th>Tipo de Uso</th>
                                            <th>D/C</th>
                                            <th>Valor (R$)</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {expandedCvaFatura.map((fat, fi) => (
                                            <tr key={fi} className={fat.debito_credito?.toUpperCase() === 'CR' ? 'lp-cva-expand-cr' : 'lp-cva-expand-db'}>
                                              <td>{fat.numero_da_fatura}</td>
                                              <td>{fat.data}</td>
                                              <td>{fat.horario}</td>
                                              <td>{summaryFieldValue(fat.placa_veiculo || record.placa_do_veiculo)}</td>
                                              <td>{fat.tipo_do_veiculo}</td>
                                              <td>{fat.descricao}</td>
                                              <td>{fat.tipo_de_uso}</td>
                                              <td>{fat.debito_credito}</td>
                                              <td className="lp-valor-cell">{formatCurrencyValue(fat.valor_rs)}</td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {visaoGeralCvaTotal > visaoGeralCvaLimit && (
                <div className="lp-pagination">
                  <button
                    className="lp-btn lp-btn-pag"
                    type="button"
                    disabled={visaoGeralCvaPage <= 1 || loadingVisaoGeralCva}
                    onClick={() => buscarVisaoGeralCva(visaoGeralCvaPage - 1, visaoGeralCvaLimit, visaoGeralCvaFiltro.trim(), visaoGeralCvaDataInicio, visaoGeralCvaDataFim, visaoGeralCvaStatusUtilizacao, visaoGeralCvaSomenteAlerta)}
                  >
                    ← Anterior
                  </button>
                  <span>Página {visaoGeralCvaPage} de {Math.ceil(visaoGeralCvaTotal / visaoGeralCvaLimit)}</span>
                  <button
                    className="lp-btn lp-btn-pag"
                    type="button"
                    disabled={visaoGeralCvaPage >= Math.ceil(visaoGeralCvaTotal / visaoGeralCvaLimit) || loadingVisaoGeralCva}
                    onClick={() => buscarVisaoGeralCva(visaoGeralCvaPage + 1, visaoGeralCvaLimit, visaoGeralCvaFiltro.trim(), visaoGeralCvaDataInicio, visaoGeralCvaDataFim, visaoGeralCvaStatusUtilizacao, visaoGeralCvaSomenteAlerta)}
                  >
                    Próxima →
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    );
  }

  if (view === 'visaoGeral') {
    return (
      <div className="levantamento-posicoes-module">
        <header className="lp-header lp-header-visao-geral">
          <div className="lp-header-content">
            <h2>Visão Geral - Passagens</h2>
            <p>Sem vale pedágio vinculado</p>
          </div>
          <button className="lp-btn-back" type="button" onClick={() => {
            setView('principal');
            setPracaSelecionada('');
          }}>
            ←
          </button>
        </header>

        <section className="lp-fatura-table-section">
          <div className="lp-vg-filter-card">
            <p className="lp-vg-filter-title">Filtros</p>
            <div className="lp-vg-filter-grid">
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-fatura">Nº Fatura</label>
                <input
                  id="lp-vg-fatura"
                  type="text"
                  value={visaoGeralFaturaFiltro}
                  onChange={event => setVisaoGeralFaturaFiltro(event.target.value)}
                  placeholder="Ex: 12345"
                />
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-placa">Placa</label>
                <input
                  id="lp-vg-placa"
                  type="text"
                  value={placaFiltro}
                  onChange={event => setPlacaFiltro(event.target.value.toUpperCase())}
                  placeholder="Ex: ABC1234"
                />
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-tipo-veiculo">Tipo Veículo</label>
                <select
                  id="lp-vg-tipo-veiculo"
                  value={visaoGeralTipoVeiculoFiltro}
                  onChange={event => setVisaoGeralTipoVeiculoFiltro(event.target.value)}
                  onFocus={carregarTiposVeiculo}
                >
                  <option value="">Todos</option>
                  {tiposVeiculoDisponiveis.map(tipo => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-data-inicio">Período de</label>
                <input
                  id="lp-vg-data-inicio"
                  type="date"
                  value={visaoGeralDataInicio}
                  onChange={event => setVisaoGeralDataInicio(event.target.value)}
                />
              </div>
              <div className="lp-vg-filter-field">
                <label htmlFor="lp-vg-data-fim">Até</label>
                <input
                  id="lp-vg-data-fim"
                  type="date"
                  value={visaoGeralDataFim}
                  onChange={event => setVisaoGeralDataFim(event.target.value)}
                />
              </div>
            </div>
            <div className="lp-vg-filter-actions">
              <button
                className="lp-btn"
                type="button"
                onClick={() => buscarVisaoGeral(1, visaoGeralLimit, { praca: '' })}
                disabled={loadingVisaoGeral}
              >
                {loadingVisaoGeral ? 'Filtrando...' : 'Filtrar'}
              </button>
              <button
                className="lp-btn lp-btn-muted"
                type="button"
                onClick={() => {
                  setPlacaFiltro('');
                  setVisaoGeralFaturaFiltro('');
                  setVisaoGeralTipoVeiculoFiltro('');
                  setVisaoGeralDataInicio('');
                  setVisaoGeralDataFim('');
                  setPracaSelecionada('');
                  buscarVisaoGeral(1, visaoGeralLimit, { placa: '', fatura: '', tipoVeiculo: '', dataInicio: '', dataFim: '' });
                }}
                disabled={loadingVisaoGeral}
              >
                Limpar
              </button>
              <button
                className="lp-btn lp-btn-export"
                type="button"
                onClick={exportarVisaoGeralCsv}
                disabled={loadingVisaoGeral || exportandoVisaoGeral || visaoGeralTotal === 0}
                title="Exportar todos os registros encontrados com os filtros atuais"
              >
                {exportandoVisaoGeral ? 'Exportando...' : 'Exportar CSV'}
              </button>
              <span className="lp-vg-result-badge">
                {pracaSelecionada 
                  ? `${visaoGeralTotal.toLocaleString('pt-BR')} registro${visaoGeralTotal !== 1 ? 's' : ''} encontrado${visaoGeralTotal !== 1 ? 's' : ''}`
                  : `${visaoGeralTotal.toLocaleString('pt-BR')} registro${visaoGeralTotal !== 1 ? 's' : ''} encontrado${visaoGeralTotal !== 1 ? 's' : ''}`
                }
              </span>
              <div className="lp-page-size-selector">
                <label htmlFor="lp-page-size-vg">Exibir:</label>
                <select
                  id="lp-page-size-vg"
                  value={visaoGeralLimit}
                  onChange={event => {
                    const newLimit = Number(event.target.value);
                    setVisaoGeralLimit(newLimit);
                    buscarVisaoGeral(1, newLimit);
                  }}
                >
                  <option value={25}>25 por página</option>
                  <option value={50}>50 por página</option>
                  <option value={100}>100 por página</option>
                  <option value={200}>200 por página</option>
                  <option value={500}>500 por página</option>
                </select>
              </div>
            </div>
          </div>

          <section className="lp-praças-indicadores">
            <h3>Passagens por Praça</h3>
            {loadingPassagensProPraca ? (
              <div className="lp-loading-message">Carregando indicadores...</div>
            ) : passagensProPraca.length === 0 ? (
              <div className="lp-no-data-mini">Nenhuma passagem encontrada</div>
            ) : (
              <div className="lp-praças-grid">
                {passagensProPraca.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.praca === 'Outras Praças' && (
                      <div
                        className={`lp-praca-card lp-praca-card-total-geral lp-praca-card-clickable ${pracaSelecionada === FILTRO_PRACA_TOTAL_VW ? 'ativa' : ''}`}
                        onClick={() => alternarFiltroPraca(FILTRO_PRACA_TOTAL_VW)}
                      >
                        <p className="lp-praca-name">Total Geral Praças Volks</p>
                        <p className="lp-praca-total">{totalPassagensGeralPracas.toLocaleString('pt-BR')}</p>
                        <p className="lp-praca-valor">{valorTotalGeralPracas.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                      </div>
                    )}
                    <div
                      className={`lp-praca-card lp-praca-card-clickable ${item.praca === 'Outras Praças' ? 'outras-pracas' : ''} ${pracaSelecionada === (item.praca === 'Outras Praças' ? FILTRO_PRACA_OUTRAS : item.praca) ? 'ativa' : ''}`}
                      onClick={() => {
                        alternarFiltroPraca(item.praca === 'Outras Praças' ? FILTRO_PRACA_OUTRAS : item.praca);
                      }}
                    >
                      <p className="lp-praca-name">{item.praca}</p>
                      <p className="lp-praca-total">{item.total.toLocaleString('pt-BR')}</p>
                      <p className="lp-praca-valor">{(item.valorTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</p>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </section>

          {visaoGeralRecords.length === 0 ? (
            <p className="lp-no-data">
              {loadingVisaoGeral ? 'Carregando...' : 'Nenhuma passagem sem vale pedágio encontrada.'}
            </p>
          ) : (
            <>
              <div className="lp-table-wrapper">
                <table className="lp-fatura-table">
                  <thead>
                    <tr>
                      <th>Nº Fatura</th>
                      <th>Data</th>
                      <th>Horário</th>
                      <th>Placa</th>
                      <th>Tipo Veículo</th>
                      <th>Descrição</th>
                      <th>Tipo Uso</th>
                      <th>Valor (R$)</th>
                      <th>Débito/Crédito</th>
                      <th>Vale Pedagio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visaoGeralRecords.map((record, idx) => (
                      <tr key={idx}>
                        <td>{summaryFieldValue(record.numero_da_fatura)}</td>
                        <td>{summaryFieldValue(record.data)}</td>
                        <td>{summaryFieldValue(record.horario)}</td>
                        <td>{summaryFieldValue((record as any).placa_veiculo)}</td>
                        <td>{summaryFieldValue(record.tipo_do_veiculo)}</td>
                        <td>{summaryFieldValue(record.descricao)}</td>
                        <td>{summaryFieldValue(record.tipo_de_uso)}</td>
                        <td className="lp-valor-cell">{summaryFieldValue(record.valor_rs)}</td>
                        <td className={`lp-debito-credito ${(record.debito_credito || '').toUpperCase() === 'CR' ? 'cr' : 'db'}`}>
                          {summaryFieldValue(record.debito_credito)}
                        </td>
                        <td className="lp-sem-vale">{summaryFieldValue(record.viagem_de_vale_pedagio)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {visaoGeralTotal > visaoGeralLimit && (
                <div className="lp-pagination">
                  <button
                    className="lp-btn lp-btn-pag"
                    type="button"
                    disabled={visaoGeralPage <= 1 || loadingVisaoGeral}
                    onClick={() => buscarVisaoGeral(visaoGeralPage - 1)}
                  >
                    ← Anterior
                  </button>
                  <span>Página {visaoGeralPage} de {Math.ceil(visaoGeralTotal / visaoGeralLimit)}</span>
                  <button
                    className="lp-btn lp-btn-pag"
                    type="button"
                    disabled={visaoGeralPage >= Math.ceil(visaoGeralTotal / visaoGeralLimit) || loadingVisaoGeral}
                    onClick={() => buscarVisaoGeral(visaoGeralPage + 1)}
                  >
                    Próxima →
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        <div ref={mapExportRef} style={{ display: 'none' }} />
      </div>
    );
  }

  return (
    <div className="levantamento-posicoes-module">
      <header className="lp-header">
        <div className="lp-header-main">
          <div className="lp-header-text">
            <h2>Levantamento Posições</h2>
            <p>
              Consulte os dados de viagem e cupons pelo CVA.
            </p>
          </div>
          <div className="lp-header-actions">
            <button
              className="lp-btn lp-btn-visao-geral lp-btn-visao-geral-header"
              type="button"
              onClick={() => buscarVisaoGeral(1)}
              disabled={loadingVisaoGeral}
            >
              {loadingVisaoGeral ? 'Carregando...' : 'Visão Geral'}
            </button>
            <button
              className="lp-btn lp-btn-visao-geral lp-btn-visao-geral-header"
              type="button"
              onClick={() => buscarVisaoGeralCva(1)}
              disabled={loadingVisaoGeralCva}
            >
              {loadingVisaoGeralCva ? 'Carregando...' : 'Visão Geral por CVA'}
            </button>
          </div>
        </div>
      </header>

      <section className="lp-controls">
        <div className="lp-controls-layout">
          <div className="lp-query-panel">
            <h3>Consulta</h3>
            <div className="lp-query-grid">
              <div>
                <label htmlFor="lp-cva-filtro">CVA</label>
                <input
                  id="lp-cva-filtro"
                  type="text"
                  value={cvaFiltro}
                  onChange={event => setCvaFiltro(event.target.value)}
                  placeholder="Digite o CVA"
                />
              </div>

              <div>
                <label htmlFor="lp-placa-filtro">Placa</label>
                <input
                  id="lp-placa-filtro"
                  type="text"
                  list="lp-placas-list"
                  value={placaFiltro}
                  onChange={event => setPlacaFiltro(event.target.value.toUpperCase())}
                  onFocus={carregarPlacas}
                  placeholder="Digite a placa"
                />
                <datalist id="lp-placas-list">
                  {placasDisponiveis.map(placa => (
                    <option key={placa} value={placa} />
                  ))}
                </datalist>
              </div>

            </div>

            <div className="lp-query-actions">
              <button className="lp-btn" type="button" onClick={buscarPosicoes} disabled={loading}>
                {loading ? 'Consultando...' : 'Buscar'}
              </button>
            </div>
          </div>

          <div className="lp-imports-panel">
            <h3>Importações</h3>

            <div className="lp-import-card">
              <label htmlFor="lp-excel-import">Planilha de Viagens</label>
              <input
                id="lp-excel-import"
                ref={excelInputRef}
                type="file"
                accept=".xlsx,.xls"
                multiple
                onChange={event => {
                  const files = Array.from(event.target.files || []);
                  setSelectedExcelFiles(files);
                }}
              />
              {selectedExcelFiles.length > 0 && (
                <span className="lp-file-name">
                  {selectedExcelFiles.length === 1
                    ? selectedExcelFiles[0].name
                    : `${selectedExcelFiles.length} arquivos selecionados`}
                </span>
              )}
              <button className="lp-btn" type="button" onClick={importarExcel} disabled={importingExcel}>
                {importingExcel ? 'Importando...' : 'Importar planilha para o banco'}
              </button>
            </div>

            <div className="lp-import-card">
              <label htmlFor="lp-fatura-excel-import">Planilha de Fatura</label>
              <input
                id="lp-fatura-excel-import"
                ref={faturaExcelInputRef}
                type="file"
                accept=".xlsx,.xls"
                multiple
                onChange={event => {
                  const files = Array.from(event.target.files || []);
                  setSelectedFaturaExcelFiles(files);
                }}
              />
              {selectedFaturaExcelFiles.length > 0 && (
                <span className="lp-file-name">
                  {selectedFaturaExcelFiles.length === 1
                    ? selectedFaturaExcelFiles[0].name
                    : `${selectedFaturaExcelFiles.length} arquivos selecionados`}
                </span>
              )}
              <button className="lp-btn" type="button" onClick={importarFaturaExcel} disabled={importingFaturaExcel}>
                {importingFaturaExcel ? 'Importando fatura...' : 'Importar fatura para o banco'}
              </button>
            </div>
          </div>
        </div>

        <div className="lp-status">{statusMessage}</div>
      </section>

      <section className="lp-manual-fields">
        <h3>Resumo Automático por CVA</h3>
        <div className="lp-cva-summary-grid">
          <div className="lp-cva-summary-item">
            <span>CVA</span>
            <strong>{summaryFieldValue(resumoCva?.cva || cvaFiltro)}</strong>
          </div>
          <div className="lp-cva-summary-item">
            <span>Vale Pedágio</span>
            <strong>{summaryFieldValue(resumoCva?.vale_pedagio)}</strong>
          </div>
          <div className="lp-cva-summary-item">
            <span>Data Pedido</span>
            <strong>{summaryFieldValue(resumoCva?.data_pedido)}</strong>
          </div>
          <div className="lp-cva-summary-item">
            <span>Cupons Concedidos</span>
            <strong>{summaryFieldValue(resumoCva?.cupons_concedidos)}</strong>
          </div>
          <div className="lp-cva-summary-item">
            <span>Cupons Utilizados (CR)</span>
            <strong>{summaryFieldValue(resumoCva?.cupons_utilizados)}</strong>
          </div>
          <div className="lp-cva-summary-item">
            <span>Cupons Não Contabilizados</span>
            <strong>{summaryFieldValue(resumoCva?.cupons_nao_contabilizados)}</strong>
          </div>
        </div>
      </section>

      <section className="lp-fatura-table-section">
        <h3>Dados da Fatura</h3>
        {faturaRecords.length === 0 ? (
          <p className="lp-no-data">Nenhum registro de fatura encontrado para este CVA.</p>
        ) : (
          <div className="lp-table-wrapper">
            <table className="lp-fatura-table">
              <thead>
                <tr>
                  <th>Nº Fatura</th>
                  <th>Data</th>
                  <th>Horário</th>
                  <th>Tipo Veículo</th>
                  <th>Descrição</th>
                  <th>Tipo Uso</th>
                  <th>Valor (R$)</th>
                  <th>Débito/Crédito</th>
                  <th>Vale Pedagio</th>
                </tr>
              </thead>
              <tbody>
                {faturaRecords.map((record, idx) => (
                  <tr key={idx}>
                    <td>{summaryFieldValue(record.numero_da_fatura)}</td>
                    <td>{summaryFieldValue(record.data)}</td>
                    <td>{summaryFieldValue(record.horario)}</td>
                    <td>{summaryFieldValue(record.tipo_do_veiculo)}</td>
                    <td>{summaryFieldValue(record.descricao)}</td>
                    <td>{summaryFieldValue(record.tipo_de_uso)}</td>
                    <td className="lp-valor-cell">{summaryFieldValue(record.valor_rs)}</td>
                    <td className={`lp-debito-credito ${(record.debito_credito || '').toUpperCase() === 'CR' ? 'cr' : 'db'}`}>
                      {summaryFieldValue(record.debito_credito)}
                    </td>
                    <td>{summaryFieldValue(record.viagem_de_vale_pedagio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div ref={mapExportRef} style={{ display: 'none' }} />
    </div>
  );
};

export default LevantamentoPosicoesModule;
